import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formsAPI, systemAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';

const TemplateCard = ({ template, onUse, disabled }) => {
  const tags = useMemo(() => (template.tags || []).slice(0, 4), [template.tags]);

  return (
    <div className="bg-card border rounded p-4 flex flex-col gap-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-foreground line-clamp-1">
            {template.title || 'Untitled Form Template'}
          </h3>
          {template.isNew && (
            <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded">
              {template.highlightLabel || 'New'}
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-xs text-muted-foreground line-clamp-3">
            {template.description}
          </p>
        )}
      </div>

      {(template.category || tags.length > 0) && (
        <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground uppercase tracking-wide">
          {template.category && (
            <span className="bg-muted px-2 py-0.5 rounded">{template.category}</span>
          )}
          {tags.map((tag) => (
            <span key={tag} className="bg-muted px-2 py-0.5 rounded">{tag}</span>
          ))}
        </div>
      )}

      <button
        className="px-3 py-2 border border-border rounded self-start text-xs font-medium hover:bg-muted transition disabled:opacity-60 disabled:cursor-not-allowed"
        onClick={() => onUse(template)}
        disabled={disabled}
      >
        {disabled ? 'Creating...' : 'Use Template'}
      </button>
    </div>
  );
};

const TemplatesGallery = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingId, setCreatingId] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemAPI.getTemplates({ type: 'form' });
      if (response.data?.success) {
        setTemplates(response.data.data || []);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('Failed to load form templates', err);
      setError(err?.response?.data?.message || err.message || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const createFrom = useCallback(async (template) => {
    if (!template) return;

    const templateId = template.id || template.slug || template.title;

    try {
      setCreatingId(templateId);

      const templateContent = template.content || {};

      const defaultPages = templateContent.pages && templateContent.pages.length > 0
        ? templateContent.pages
        : [{ id: 'page-1', title: 'Page 1', elements: [] }];

      const createResponse = await formsAPI.create({
        name: template.title || 'Untitled form',
        pages: defaultPages
      });

      const newFormId = createResponse.data?.data?._id;

      if (!newFormId) {
        throw new Error('Form could not be created from template');
      }

      const updatePayload = {};

      if (templateContent.pages) updatePayload.pages = templateContent.pages;
      if (templateContent.settings) updatePayload.settings = templateContent.settings;
      if (templateContent.theme) updatePayload.theme = templateContent.theme;
      if (templateContent.logic) updatePayload.logic = templateContent.logic;
      if (templateContent.mapping) updatePayload.mapping = templateContent.mapping;
      if (templateContent.scoring) updatePayload.scoring = templateContent.scoring;
      if (templateContent.limits) updatePayload.limits = templateContent.limits;
      if (templateContent.localeDefault) updatePayload.localeDefault = templateContent.localeDefault;
      if (Array.isArray(templateContent.locales)) updatePayload.locales = templateContent.locales;
      if (templateContent.access) updatePayload.access = templateContent.access;

      if (Object.keys(updatePayload).length > 0) {
        await formsAPI.update(newFormId, updatePayload);
      }

      toast.success('Template copied to your workspace.');
      navigate(`/forms/${newFormId}/builder`);
    } catch (err) {
      console.error('Failed to create form from template', err);
      toast.error(err?.response?.data?.message || err.message || 'Failed to create form from template');
    } finally {
      setCreatingId(null);
    }
  }, [navigate]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Form Templates</h1>
        {!loading && (
          <button
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition"
            onClick={loadTemplates}
          >
            Refresh
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-10 text-center">Loading templates...</div>
      ) : error ? (
        <div className="text-sm text-destructive py-10 text-center">{error}</div>
      ) : templates.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center">
          No published templates available yet. Check back soon or create your own forms from scratch.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onUse={createFrom}
              disabled={creatingId === (template.id || template.slug || template.title)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatesGallery;

